import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash, Cpu, WifiHigh, WifiSlash } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable device-assignment editor — wires Device Registry devices to a shipment.
 *
 * Two modes (same pattern as GeofenceAssignmentsEditor):
 *   - Pending mode (`shipmentId` null): tracks selected device ids in local state
 *     and surfaces them via `onChange`. Parent flushes them after shipment create
 *     via POST `/api/device-registry/{id}/assign-shipment`.
 *   - Live mode (`shipmentId` set): each add/remove hits the backend immediately
 *     and reloads via `onAfterChange`.
 */
export default function DeviceAssignmentsEditor({
  shipmentId = null,
  value: pendingValue,
  onChange,
  initialDeviceIds = [],
  onAfterChange,
}) {
  const live = !!shipmentId;
  const [devices, setDevices] = useState([]);          // ALL devices in registry
  const [selectedIds, setSelectedIds] = useState(initialDeviceIds || []);
  const [draftId, setDraftId] = useState("");

  useEffect(() => {
    if (!live && Array.isArray(pendingValue)) setSelectedIds(pendingValue);
  }, [pendingValue, live]);

  useEffect(() => {
    axios.get(`${API}/device-registry`).then((r) => setDevices(r.data.items || [])).catch(() => setDevices([]));
  }, []);

  // In live mode, derive selected ids from assignment field on devices
  useEffect(() => {
    if (live && shipmentId && devices.length > 0) {
      const ids = devices.filter((d) => d.assigned_shipment_id === shipmentId).map((d) => d.id);
      setSelectedIds(ids);
    }
  }, [live, shipmentId, devices]);

  const dById = (id) => devices.find((d) => d.id === id);
  const available = devices.filter((d) =>
    !selectedIds.includes(d.id) && (live ? !d.assigned_shipment_id || d.assigned_shipment_id === shipmentId : true)
  );

  const add = async () => {
    if (!draftId) return;
    if (live) {
      await axios.post(`${API}/device-registry/${draftId}/assign-shipment`, { shipment_id: shipmentId });
      const next = [...selectedIds, draftId];
      setSelectedIds(next);
      onAfterChange?.(next);
    } else {
      const next = [...selectedIds, draftId];
      setSelectedIds(next);
      onChange?.(next);
    }
    setDraftId("");
  };

  const remove = async (id) => {
    if (live) {
      await axios.post(`${API}/device-registry/${id}/assign-shipment`, { shipment_id: null });
      const next = selectedIds.filter((x) => x !== id);
      setSelectedIds(next);
      onAfterChange?.(next);
    } else {
      const next = selectedIds.filter((x) => x !== id);
      setSelectedIds(next);
      onChange?.(next);
    }
  };

  return (
    <div data-testid="device-assignments-editor">
      <Label className="text-[10px] uppercase tracking-wider text-zinc-400 flex items-center gap-1 mb-2">
        <Cpu size={11} weight="duotone" /> Registered Devices
        <span className="text-zinc-600 normal-case font-normal tracking-normal ml-1">
          ({selectedIds.length})
        </span>
      </Label>

      {/* Add row */}
      <div className="grid grid-cols-12 gap-2 mb-2">
        <select value={draftId} onChange={(e) => setDraftId(e.target.value)}
          className="col-span-11 bg-zinc-900 border border-zinc-800 text-zinc-200 text-xs rounded-sm px-2 h-8"
          data-testid="dae-device">
          <option value="">— Select device from registry —</option>
          {available.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name} {d.serial ? `· ${d.serial}` : ""} ({d.device_type})
            </option>
          ))}
        </select>
        <Button type="button" onClick={add} disabled={!draftId}
          className="col-span-1 bg-teal-500 hover:bg-teal-400 text-zinc-950 text-xs h-8 px-2"
          data-testid="dae-add">
          <Plus size={12} />
        </Button>
      </div>

      <div className="space-y-1">
        {selectedIds.length === 0 ? (
          <p className="text-[10px] text-zinc-600 italic">No devices from registry attached to this shipment.</p>
        ) : selectedIds.map((id) => {
          const d = dById(id);
          return (
            <div key={id}
              className="flex items-center gap-2 px-2 py-1.5 bg-zinc-900/60 border border-zinc-800 rounded-sm text-xs"
              data-testid={`dae-row-${id}`}>
              {d?.online
                ? <WifiHigh size={12} weight="bold" className="text-emerald-400" />
                : <WifiSlash size={12} weight="bold" className="text-zinc-600" />}
              <span className="text-zinc-200 truncate flex-1 font-medium">{d?.name || id}</span>
              {d?.device_type && <span className="text-[10px] text-zinc-500 font-mono">{d.device_type}</span>}
              {d?.serial && <span className="text-[10px] text-zinc-500 font-mono">SN: {d.serial}</span>}
              <button type="button" onClick={() => remove(id)}
                className="text-zinc-500 hover:text-red-300 p-1"
                data-testid={`dae-remove-${id}`}>
                <Trash size={11} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
